<template>
  <div>
    <PurchaseFlowTabs
      :tabs="suitabilityTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
      :is-review="is_review"
      :is-modal-edit="is_modal_edit"
    />
    <div
      v-if="is_modal_edit && s1FinancialIssueMessages.length"
      class="mt-5 md:mt-6 p-4 rounded-2xl border border-error bg-container-bg animate-slideup"
    >
      <div class="flex items-start gap-3">
        <Icon name="Asterisk" class="text-error min-h-6 min-w-6 -skew-x-12 mt-1" />
        <div class="text-sm text-error font-medium">
          <p class="font-inter font-semibold mb-2">Please review and correct the following before saving:</p>
          <ul class="list-disc list-inside space-y-1">
            <li
              v-for="msg in s1FinancialIssueMessages"
              :key="msg"
              class="font-inter"
            >
              {{ msg }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Time estimate — only on the paginated forward flow, to counter the "one long step" feel -->
    <div v-if="paginate" class="mt-4 flex items-center gap-2 text-strong">
      <Icon name="Clock" class="h-4 w-4" />
      <p class="text-sm font-inter font-medium">Takes about 3–4 minutes</p>
    </div>

    <!-- Financial Profile group (income fields via the standard Form) -->
    <div v-if="!paginate || currentGroup?.renderer === 'form'" class="mt-5 md:mt-6">
      <h2 class="font-semibold font-inter text-strongest text-lg mb-3">Financial Information</h2>
      <div
        v-if="purchaseFlowStore.selected_offering?.type === 'S1' && !is_review"
        class="p-4 rounded-xl bg-container-bg mb-3 animate-slideup"
      >
        <AsteriskNotice variant="info" size="md" class="mb-1" text="Important Notice" />
        <div class="font-inter text-strongest font-medium">
          We will not require supporting documentation to verify the financial information you
          provided in this section.
        </div>
      </div>
      <!-- Plain wrapper (not PaperCard) so the Source of Income dropdown's absolutely-positioned
           option list isn't clipped by PaperCard's overflow-hidden collapse wrapper. -->
      <div class="rounded-xl bg-container-bg py-5 px-4">
        <Form
          :schema_array="incomeSchema"
          :state_object="dataStore.contact.suitability_info"
          :record_id="dataStore.contact?.record_id"
          :is_disabled="is_review"
          variant="primary"
        />
      </div>
    </div>

    <!-- Suitability questions: full list for review/modal, one group at a time when paginated -->
    <div v-if="!paginate" class="mt-5 md:mt-6">
      <h2 class="font-semibold font-inter text-strongest text-lg mb-3">Suitability Questions</h2>
      <SuitabilityQuestionnaire
        ref="questionnaireRef"
        :schema_array="questionnaireSchema"
        :variant="is_review ? 'primary' : 'secondary'"
        :is_disabled="is_review"
        :show_errors="is_modal_edit && !!purchaseFlowStore.suitability_review_error"
        :hide_controls="is_review || is_modal_edit"
        @continue="emit('continue')"
      />
    </div>
    <div v-else-if="currentGroup?.renderer === 'questionnaire'" class="mt-5 md:mt-6">
      <SuitabilityQuestionnaire
        ref="questionnaireRef"
        :key="currentGroup.name"
        :schema_array="currentGroupSchema"
        :group_label="currentGroup.name"
        variant="secondary"
        @group-complete="handleGroupComplete"
        @continue="emit('continue')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  useSchemaStore,
  useDataStore,
  usePurchaseFlowStore,
  useUIStore,
  useChangeInvestmentStore,
} from "@/stores";
import { Form, SuitabilityQuestionnaire, Icon, AsteriskNotice } from "@/components";
import { SUITABILITY_QUESTIONNAIRE_GROUPS } from "@/constants";
import { partitionQuestionnaireGroups } from "@/utilities";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";

const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const changeInvestmentStore = useChangeInvestmentStore();
const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();

const route = useRoute();

const emit = defineEmits(["substep-change", "continue"]);
const props = defineProps({
  is_review: { type: Boolean, default: false },
  is_modal_edit: { type: Boolean, default: false },
});

const questionnaireRef = ref(null);
const localSubstep = ref(1);

// Paginate the section into grouped sub-steps only in the forward flow. The review step and
// the review-edit modal keep the full "everything at once" rendering.
const paginate = computed(() => !props.is_review && !props.is_modal_edit);

const suitabilityTabs = computed(() =>
  paginate.value
    ? SUITABILITY_QUESTIONNAIRE_GROUPS.map((g) => ({ name: g.name, schema_group: "Questionnaire" }))
    : [{ name: "Questionnaire", schema_group: "Questionnaire" }]
);

onMounted(() => {
  if (props.is_modal_edit && (purchaseFlowStore.suitability_review_error || s1FinancialIssueMessages.value.length > 0)) {
    schemaStore.validateFields(
      dataStore.contact,
      incomeSchema.value,
      dataStore.contact.record_id,
      true
    );
  }
});

const isChangeInvestementFlow = computed(() => route.path.startsWith("/change-investment"));

const currentSubstepIndex = computed(() => {
  if (props.is_review || props.is_modal_edit) return localSubstep.value - 1;
  return isChangeInvestementFlow.value
    ? changeInvestmentStore.current_substep - 1
    : purchaseFlowStore.current_substep - 1;
});

const incomeSchema = computed(
  () =>
    purchaseFlowStore.suitability_questions.find((s) => s.group_name === "Income Information")
      ?.fields
);

// To re-enable a question, remove its field_name from this array
const EXCLUDED_QUESTIONNAIRE_FIELDS = ["self_responsibility"];

const questionnaireSchema = computed(() => {
  const fields =
    purchaseFlowStore.suitability_questions.find((s) => s.group_name === "Questionnaire")?.fields ??
    [];
  return fields.filter((f) => !EXCLUDED_QUESTIONNAIRE_FIELDS.includes(f.field_name));
});

// Partition the questionnaire fields into their display groups (see the helper for the
// catch-all rule that keeps unlisted fields from being dropped).
const groupedQuestionnaire = computed(() =>
  partitionQuestionnaireGroups(questionnaireSchema.value, SUITABILITY_QUESTIONNAIRE_GROUPS)
);

const currentGroup = computed(() => groupedQuestionnaire.value[currentSubstepIndex.value]);
const currentGroupSchema = computed(() => currentGroup.value?.schema ?? []);

const s1FinancialIssueMessages = computed(() => {
  const offering = purchaseFlowStore.selected_offering;
  if (offering?.type !== "S1") return [];

  const isShortTerm = purchaseFlowStore.current_investment?.is_short_term === true;
  const rawAmt = purchaseFlowStore.current_investment?.amount;
  const investmentAmount =
    typeof rawAmt === "string" ? Number(rawAmt.replace(/,/g, "")) : Number(rawAmt) || 0;

  if (isShortTerm && investmentAmount < 10000) return [];

  const thresholds = isShortTerm
    ? { income: 45000, netWorth: 45000, netWorthSolo: 150000 }
    : { income: 70000, netWorth: 70000, netWorthSolo: 250000 };

  const info = dataStore.contact.suitability_info || {};
  const income = Number(String(info.current_income || "").replace(/,/g, "")) || 0;
  const netWorth = Number(String(info.current_net_worth || "").replace(/,/g, "")) || 0;
  const fmt = (n) => `$${n.toLocaleString()}`;

  const messages = [];
  if (!((income >= thresholds.income && netWorth >= thresholds.netWorth) || netWorth >= thresholds.netWorthSolo)) {
    messages.push(
      `For this${isShortTerm ? " flex term" : ""} Registered Offering (S-1), your current annual income and total net worth must each be at least ${fmt(thresholds.income)}, or your total net worth must be at least ${fmt(thresholds.netWorthSolo)}.`
    );
  }
  return messages;
});

function handleTabClick(index) {
  const targetSubstep = index + 1;
  const currentSubstep = localSubstep.value;

  if (props.is_review) {
    localSubstep.value = targetSubstep;
    return;
  }

  if (props.is_modal_edit) {
    if (targetSubstep <= currentSubstep) {
      localSubstep.value = targetSubstep;
      return;
    }

    const result = validateRange(currentSubstep - 1, index);
    if (!result.proceed) {
      localSubstep.value = result.failedAt + 1;
      return;
    }
    localSubstep.value = targetSubstep;
    return;
  }

  emit("substep-change", targetSubstep);
}

// Auto-advance to the next group once the current question group is fully answered.
function handleGroupComplete() {
  if (!paginate.value) return;
  if (currentSubstepIndex.value + 1 < suitabilityTabs.value.length) {
    const nextSubstep = currentSubstepIndex.value + 2; // 1-based next sub-step
    setTimeout(() => emit("substep-change", nextSubstep), 400);
  }
}

function getValidationErrorMessage(result) {
  if (result.source === "income") {
    return "Please complete all income fields before continuing.";
  }
  if (result.answeredCount !== undefined && result.answeredCount < result.totalQuestions) {
    return "Please answer all questions before continuing.";
  }
  return "Please complete the questionnaire before continuing.";
}

function hasEmptyIncome(schema) {
  return (schema || []).some((field) => {
    if (!field.is_required) return false;
    const value = dataStore.contact?.suitability_info?.[field.field_name];
    return value === null || value === undefined || value === "";
  });
}

function countAnswered(schema) {
  return (schema || []).filter((f) => {
    const v = dataStore.contact.suitability_info?.[f.field_name];
    return v !== null && v !== undefined && v !== "";
  }).length;
}

// Validate a questionnaire group directly from the store — used when validating a group that
// isn't the one currently mounted (e.g. jumping several tabs forward at once).
function directQuestionnaireGroupValid(schema) {
  const info = dataStore.contact.suitability_info || {};
  const allAnswered = (schema || []).every((f) => {
    const v = info[f.field_name];
    return v !== null && v !== undefined && v !== "";
  });
  if (!allAnswered) return false;

  if (schema.some((f) => f.field_name === "employment_status")) {
    const emp = info.employment_status;
    if (
      (emp === "Employed" || emp === "Self-Employed") &&
      (!dataStore.contact.occupation?.trim() || !dataStore.contact.employer?.trim())
    ) {
      return false;
    }
  }
  if (schema.some((f) => f.field_name === "employed_by_security_or_financial_regulatory")) {
    if (
      info.employed_by_security_or_financial_regulatory === "Yes" &&
      (!dataStore.contact.securities_employer_name?.trim() ||
        !dataStore.contact.securities_employer_address?.trim())
    ) {
      return false;
    }
  }
  return true;
}

function validateSubstep(index) {
  const tab = suitabilityTabs.value[index];
  const group = paginate.value ? groupedQuestionnaire.value[index] : null;

  // Non-paginated (review / modal edit): validate income + full questionnaire together.
  if (!paginate.value) {
    if (props.is_modal_edit) {
      schemaStore.validateFields(
        dataStore.contact,
        incomeSchema.value,
        dataStore.contact.record_id,
        true
      );
    }
    if (hasEmptyIncome(incomeSchema.value)) {
      return { proceed: false, tabName: tab?.name, source: "income" };
    }
    if (props.is_modal_edit && s1FinancialIssueMessages.value.length > 0) {
      return { proceed: false, tabName: tab?.name, source: "financial" };
    }
    if (questionnaireRef.value) {
      const r = questionnaireRef.value.validate();
      return {
        proceed: r.proceed,
        error_messages: r.error_messages,
        tabName: tab?.name,
        answeredCount: r.answeredCount,
        totalQuestions: r.totalQuestions,
      };
    }
    return { proceed: true, tabName: tab?.name };
  }

  // Paginated Financial Profile group: income fields only.
  if (group?.renderer === "form") {
    if (hasEmptyIncome(incomeSchema.value)) {
      return { proceed: false, tabName: tab?.name, source: "income" };
    }
    return { proceed: true, tabName: tab?.name };
  }

  // Paginated questionnaire group.
  const groupSchema = group?.schema ?? [];
  if (index === currentSubstepIndex.value && questionnaireRef.value) {
    // Currently mounted group — use the child so it flips error highlights + enables scroll.
    const r = questionnaireRef.value.validate();
    return {
      proceed: r.proceed,
      error_messages: r.error_messages,
      tabName: tab?.name,
      answeredCount: r.answeredCount,
      totalQuestions: r.totalQuestions,
    };
  }
  return {
    proceed: directQuestionnaireGroupValid(groupSchema),
    tabName: tab?.name,
    answeredCount: countAnswered(groupSchema),
    totalQuestions: groupSchema.length,
  };
}

function validateRange(fromIndex, toIndex) {
  for (let i = fromIndex; i < toIndex; i++) {
    const result = validateSubstep(i);
    if (!result.proceed) {
      return {
        proceed: false,
        failedAt: i,
        invalid_fields: result.invalid_fields,
        tabName: result?.tabName,
        error_messages: result.error_messages,
        field_errors: result.field_errors,
      };
    }
  }
  return { proceed: true };
}

defineExpose({
  async validate() {
    const result = validateSubstep(currentSubstepIndex.value);

    if (!result.proceed) {
      // If a questionnaire group failed, scroll to its first error.
      if (result.answeredCount !== undefined) {
        questionnaireRef.value?.scrollToFirstError();
      }

      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: getValidationErrorMessage(result),
      });
    }

    return result;
  },
  validateRange,
  afterContinue: async () => await purchaseFlowStore.saveOwnerProgress(isChangeInvestementFlow),
  // The forward flow derives the sub-step count from the store's `sub_steps`; this is only
  // read by the review-edit modal, which always renders everything at once (validate once).
  substepCount: 1,
});
</script>
